import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function StewardHandoffPanel({ caseData, analysis }) {
  const [handoff, setHandoff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  async function generate() {
    if (!caseData?.narrative || caseData.narrative.length < 10) {
      setError('Need a narrative to generate the handoff packet.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/fact-finder-v2/steward-handoff`, {
        case_data: caseData,
        analysis_data: analysis || null,
      });
      if (res.data.success) {
        setHandoff(res.data.handoff);
      } else {
        setError(res.data.error || 'Failed to generate handoff packet.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate handoff packet.');
    } finally {
      setLoading(false);
    }
  }

  async function copyToClipboard() {
    if (!handoff) return;
    const text = formatHandoffText(handoff);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { setError('Copy failed'); }
  }

  function formatHandoffText(h) {
    let txt = '=== STEWARD HANDOFF PACKET ===\n\n';
    txt += `CASE SUMMARY:\n${h.case_summary}\n\n`;
    if (h.key_facts?.length) txt += `KEY FACTS:\n${h.key_facts.map(f => `- ${f}`).join('\n')}\n\n`;
    if (h.timeline_summary) txt += `TIMELINE:\n${h.timeline_summary}\n\n`;
    if (h.contract_references?.length) txt += `CONTRACT REFERENCES:\n${h.contract_references.map(c => `- ${c.article}: ${c.relevance}`).join('\n')}\n\n`;
    if (h.management_actions?.length) txt += `MANAGEMENT ACTIONS:\n${h.management_actions.map(a => `- ${a}`).join('\n')}\n\n`;
    if (h.worker_concerns?.length) txt += `WORKER CONCERNS:\n${h.worker_concerns.map(c => `- ${c}`).join('\n')}\n\n`;
    if (h.recommended_steward_actions?.length) txt += `RECOMMENDED ACTIONS:\n${h.recommended_steward_actions.map(a => `- [${a.priority?.toUpperCase()}] ${a.action} — ${a.reason}`).join('\n')}\n\n`;
    if (h.documents_needed?.length) txt += `DOCUMENTS NEEDED:\n${h.documents_needed.map(d => `- ${d}`).join('\n')}\n\n`;
    if (h.risk_assessment) txt += `RISK ASSESSMENT:\n${h.risk_assessment}\n\n`;
    if (h.next_steps) txt += `NEXT STEPS:\n${h.next_steps}\n`;
    return txt;
  }

  return (
    <div className="ff-card" data-testid="steward-handoff-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>Steward Handoff Packet</h2>
          <p className="ff-card-description" style={{ margin: '4px 0 0' }}>Generate a comprehensive handoff document for your union steward.</p>
        </div>
        {!handoff && (
          <button className="ff-btn-primary" onClick={generate} disabled={loading} data-testid="generate-handoff-btn">
            {loading ? 'Generating...' : 'Generate Packet'}
          </button>
        )}
      </div>

      {error && <div className="ff-error-message" style={{ marginTop: 10 }}>{error}</div>}

      {handoff && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className="ff-btn-secondary" onClick={copyToClipboard} data-testid="copy-handoff-btn" style={{ fontSize: 12 }}>
              {copied ? 'Copied!' : 'Copy All'}
            </button>
            <button className="ff-btn-secondary" onClick={generate} disabled={loading} style={{ fontSize: 12 }}>
              {loading ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>

          {/* Case Summary */}
          <Section title="Case Summary" data-testid="handoff-summary">{handoff.case_summary}</Section>

          {/* Key Facts */}
          {handoff.key_facts?.length > 0 && (
            <Section title="Key Facts">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {handoff.key_facts.map((f, i) => <li key={i} style={{ marginBottom: 4 }}>{f}</li>)}
              </ul>
            </Section>
          )}

          {/* Timeline */}
          {handoff.timeline_summary && <Section title="Timeline Summary">{handoff.timeline_summary}</Section>}

          {/* Contract References */}
          {handoff.contract_references?.length > 0 && (
            <Section title="Contract References">
              {handoff.contract_references.map((c, i) => (
                <div key={i} style={{ marginBottom: 6, padding: '6px 10px', background: '#0f172a', borderRadius: 6, border: '1px solid #334155' }}>
                  <span style={{ color: '#3b82f6', fontWeight: 600 }}>{c.article}</span>
                  <span style={{ color: '#94a3b8', marginLeft: 8 }}>{c.relevance}</span>
                </div>
              ))}
            </Section>
          )}

          {/* Recommended Actions */}
          {handoff.recommended_steward_actions?.length > 0 && (
            <Section title="Recommended Steward Actions">
              {handoff.recommended_steward_actions.map((a, i) => (
                <div key={i} style={{ marginBottom: 6, padding: '6px 10px', background: '#0f172a', borderRadius: 6, border: '1px solid #334155' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, marginRight: 8,
                    color: a.priority === 'high' ? '#ef4444' : a.priority === 'medium' ? '#f59e0b' : '#64748b',
                    background: a.priority === 'high' ? '#ef444418' : a.priority === 'medium' ? '#f59e0b18' : '#64748b18',
                  }}>{(a.priority || 'medium').toUpperCase()}</span>
                  <span style={{ color: '#e2e8f0', fontWeight: 500 }}>{a.action}</span>
                  {a.reason && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{a.reason}</div>}
                </div>
              ))}
            </Section>
          )}

          {/* Management Actions */}
          {handoff.management_actions?.length > 0 && (
            <Section title="Management Actions So Far">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {handoff.management_actions.map((a, i) => <li key={i} style={{ marginBottom: 4 }}>{a}</li>)}
              </ul>
            </Section>
          )}

          {/* Documents Needed */}
          {handoff.documents_needed?.length > 0 && (
            <Section title="Documents Needed">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {handoff.documents_needed.map((d, i) => <li key={i} style={{ marginBottom: 4 }}>{d}</li>)}
              </ul>
            </Section>
          )}

          {/* Risk & Next Steps */}
          {handoff.risk_assessment && <Section title="Risk Assessment">{handoff.risk_assessment}</Section>}
          {handoff.next_steps && <Section title="Next Steps">{handoff.next_steps}</Section>}
        </div>
      )}
    </div>
  );
}

function Section({ title, children, ...props }) {
  return (
    <div style={{ marginBottom: 12 }} {...props}>
      <h3 style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</h3>
      <div style={{ color: '#e2e8f0', fontSize: 13, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

export default StewardHandoffPanel;
